import React, { useContext, useState } from 'react';
import styled from 'styled-components';

import Badge from 'kingpin/atoms/Badge';
import Button from 'kingpin/atoms/Button';
import TextInput from 'kingpin/atoms/TextInput';
import Typography from 'kingpin/atoms/Typography';
import Row from 'components/Common/Row';
import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import ToastContext from 'contexts/ToastContext';
import { testAccountKbTriggers } from 'services/agentKb';

// "If someone asked this, what would the agent be told?" — answered by the REAL
// server-side matcher, so the answer is what will actually happen rather than a
// client-side guess at the same rules.
//
// Note it tests the QUESTION only: triggers that key on what is on screen (a
// metric, a report, a workspace) can't fire here, and the empty result for one
// of those is correct rather than a bug.

const Wrapper = styled.div`
  border-top: 1px solid var(--color-border-secondary);
  padding: 16px 32px;
`;

const ResultRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
`;

const TestTriggersBox = () => {
  const { api } = useContext(CloudFunctionClientContext);
  const { showToast } = useContext(ToastContext);
  const [question, setQuestion] = useState<string>('');
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [result, setResult] = useState<{
    fired: AccountKb.TriggerMatch[];
    nearMissed: AccountKb.TriggerMatch[];
  } | null>(null);

  const onTest = async () => {
    setIsRunning(true);
    const response = await testAccountKbTriggers(api, { question });
    setIsRunning(false);
    if (response.ok) {
      setResult({
        fired: response.data.fired,
        nearMissed: response.data.nearMissed,
      });
    } else {
      showToast(response.error);
    }
  };

  return (
    <Wrapper data-testid="account-kb-test-triggers">
      <Typography.Body type="Label">Test a question</Typography.Body>
      <Row centerAlign style={{ gap: 8, margin: '8px 0' }}>
        <TextInput
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          placeholder="Why is on-time delivery down?"
          data-testid="account-kb-test-question"
        />
        <Button
          type="Secondary"
          size="Small"
          label={isRunning ? 'Checking…' : 'Check'}
          isDisabled={isRunning || question.trim() === ''}
          onClick={onTest}
        />
      </Row>
      {result &&
        result.fired.length === 0 &&
        result.nearMissed.length === 0 && (
          <Typography.Body type="Body 12" color="var(--color-text-secondary)">
            Nothing fires on this question. Triggers that key on a metric,
            report or workspace can only fire from the chat, where that context
            exists.
          </Typography.Body>
        )}
      {result?.fired.map((match) => (
        <ResultRow key={match.sectionId}>
          <Badge badgeType="Green" text="Fires" />
          <Typography.Body type="Body 13">{match.title}</Typography.Body>
          <Typography.Body type="Body 12" color="var(--color-text-secondary)">
            {match.matched.join(', ')}
          </Typography.Body>
        </ResultRow>
      ))}
      {result?.nearMissed.map((match) => (
        <ResultRow key={match.sectionId}>
          <Badge badgeType="Orange" text="Capped out" />
          <Typography.Body type="Body 13">{match.title}</Typography.Body>
          <Typography.Body type="Body 12" color="var(--color-text-secondary)">
            matched {match.matched.join(', ')} but lost to the per-answer cap
          </Typography.Body>
        </ResultRow>
      ))}
    </Wrapper>
  );
};

export default TestTriggersBox;
